import { Button, Link, Typography } from '@iheartjane/reefer';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import type { SortOptions } from '@jane/bloom/types/schemas/algolia/product';
import { SORT_ORDER_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';

import { ModalSection } from './modalSection';

import styles from './sortSection.module.css';

interface SortSectionProps {
  currentSort: SortOptions;
  isMobile?: boolean;
  sortOptions: { label: string; value: SortOptions }[];
}

export function SortSection({
  currentSort,
  isMobile = false,
  sortOptions,
}: SortSectionProps) {
  const [, setSearchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(true);

  const changeSort = useCallback(
    (value: string) =>
      setSearchParams((prev) => {
        prev.set(SORT_ORDER_QUERY_KEY, value);
        return prev;
      }),
    [setSearchParams]
  );

  const sortOptionsToDisplay = useMemo(() => {
    return collapsed && isMobile ? sortOptions.slice(0, 4) : sortOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  const isCollapsible = sortOptions.length > 4 && isMobile;

  return (
    <ModalSection
      filterLabel="Sort"
      deselectFilters={() => null}
      shouldShowDeselectOption={false}
    >
      <div className={styles['buttons']}>
        {sortOptionsToDisplay.map(({ label, value }) => (
          <Button
            className={styles['button']}
            key={value}
            full
            size="large"
            alignment="left"
            label={
              <Typography variant="body-semibold" color="inherit">
                {label}
              </Typography>
            }
            variant={currentSort === value ? 'tertiary-selected' : 'tertiary'}
            onClick={() => {
              changeSort(value);
            }}
          />
        ))}
      </div>

      {isCollapsible && (
        <Link
          mt={16}
          color="text-main"
          onClick={() => {
            setCollapsed(!collapsed);
          }}
        >
          {collapsed ? 'View more' : 'View less'}
        </Link>
      )}
    </ModalSection>
  );
}
